import React, { useState, useEffect, useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { getPartnerAccountsDomains } from '@atlassian/temp-proxies';
import { useApplicationContext, useFeatureToggle } from '@css-xp/base';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';

interface DomainInfo {
  id: string;
  domain: string;
  alternateDomains: string[];
  relatedDomains: string[];
}

export interface PartnerDomainContextProviderState {
  data: DomainInfo | null;
  isLoading: boolean;
  error: Error | null;
}

export const PartnerDomainContext = React.createContext<PartnerDomainContextProviderState>({
  data: null,
  isLoading: false,
  error: null,
});

interface PartnerDomainContextProviderProps {
  children: JSX.Element | JSX.Element[];
}

export const PartnerDomainContextProvider = ({ children }: PartnerDomainContextProviderProps) => {
  const [domainsInfo, setDomainsInfo] = useState<PartnerDomainContextProviderState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const [isEnabled] = useFeatureToggle();
  const isSFDCDataCachingEnabledForPartnerProfile = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SFDC_DATA_CACHING_FOR_PARTNER_PROFILE
  );

  const user = useApplicationContext();
  const [txaId] = useLocalStorage(`${user?.atlassianAccountId}-selectedTxa`, '');

  const selectedPartnerAccountId = user.partnerAccounts.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  )?.id;

  const fetchDomains = useCallback(async () => {
    setDomainsInfo({
      data: null,
      isLoading: true,
      error: null,
    });
    try {
      const domainData = await getPartnerAccountsDomains([selectedPartnerAccountId]);
      if (Array.isArray(domainData) && domainData.length >= 1) {
        setDomainsInfo({
          data: domainData[0],
          isLoading: false,
          error: null,
        });
      } else {
        throw new Error('Invalid partner account for the user');
      }
    } catch (err) {
      setDomainsInfo({
        data: null,
        isLoading: false,
        error: err as Error,
      });
    }
  }, [selectedPartnerAccountId]);

  useEffect(() => {
    if (isSFDCDataCachingEnabledForPartnerProfile && selectedPartnerAccountId) {
      fetchDomains();
    }
  }, [fetchDomains, isSFDCDataCachingEnabledForPartnerProfile, selectedPartnerAccountId]);

  return <PartnerDomainContext.Provider value={domainsInfo}>{children}</PartnerDomainContext.Provider>;
};

export const usePartnerDomainContext = () => {
  const context = React.useContext(PartnerDomainContext);

  if (!context) {
    // eslint-disable-next-line no-console
    console.error('Partner domain context has not been setup');
  }

  return context;
};
